/**
 * Agent autonomy settings — per-action-kind trust levels.
 *
 * Keeps track of which action kinds are allowed to auto-execute (vs. always
 * requiring manual confirmation), and maintains a streak count of successful
 * confirmations per kind. When a kind hits the PROMOTION_THRESHOLD while still
 * in 'propose' mode, the UI can show a suggestion chip to "Enable auto" for that
 * kind.
 *
 * Persisted to localStorage (key: 'argo-os:autonomy') as a JSON map so autonomy
 * preferences survive reloads. Stored per-operator, not shared data — each user
 * decides their own comfort with auto-run for each action kind.
 */

import type { AgentAction } from './types';

/** Trust level per action kind: 'propose' = always ask, 'auto' = execute immediately */
export type AutonomyLevel = 'propose' | 'auto';

export const PROMOTION_THRESHOLD = 10;

const STORAGE_KEY = 'argo-os:autonomy';

/**
 * Shape of the persisted autonomy record. Flat map of action kind → {level, streak}.
 */
interface AutonomyState {
  [kind: string]: {
    level: AutonomyLevel;
    streak: number;
  };
}

/**
 * Read the entire autonomy state from localStorage. Returns a fresh map if
 * localStorage is absent or corrupt.
 */
function readAutonomyState(): AutonomyState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Persist the autonomy state to localStorage. Idempotent — errors are swallowed
 * so a storage failure doesn't crash the app.
 */
function writeAutonomyState(state: AutonomyState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/**
 * Get the autonomy level (trust setting) for a given action kind.
 * Defaults to 'propose' (always ask).
 */
export function getAutonomy(kind: AgentAction['kind']): AutonomyLevel {
  const state = readAutonomyState();
  return state[kind]?.level ?? 'propose';
}

/**
 * Get the confirmation streak count for a given action kind.
 * Defaults to 0 (no streak). A streak indicates how many times the user has
 * manually confirmed that action kind in a row.
 */
export function getStreak(kind: AgentAction['kind']): number {
  const state = readAutonomyState();
  return state[kind]?.streak ?? 0;
}

/**
 * Set the autonomy level for a given action kind ('propose' or 'auto').
 */
export function setAutonomy(kind: AgentAction['kind'], level: AutonomyLevel): void {
  const state = readAutonomyState();
  state[kind] = { ...state[kind], level };
  writeAutonomyState(state);
  notifyListeners();
}

/**
 * Record a successful confirmation of an action kind. Increments the streak
 * for that kind. Called when the user manually confirms an action.
 */
export function recordConfirmStreak(kind: AgentAction['kind']): void {
  const state = readAutonomyState();
  const streak = (state[kind]?.streak ?? 0) + 1;
  state[kind] = { ...state[kind], streak, level: state[kind]?.level ?? 'propose' };
  writeAutonomyState(state);
  notifyListeners();
}

/**
 * Reset the streak counter for a given action kind back to 0.
 * Called when the user dismisses the promotion suggestion.
 */
export function resetStreak(kind: AgentAction['kind']): void {
  const state = readAutonomyState();
  if (state[kind]) {
    state[kind].streak = 0;
  }
  writeAutonomyState(state);
  notifyListeners();
}

// --- Listener pattern for reactive updates ---

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Subscribe to autonomy changes. Returns an unsubscribe function.
 * Listeners are notified whenever autonomy settings or streaks change.
 */
export function subscribeAutonomy(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notifyListeners(): void {
  for (const fn of listeners) {
    fn();
  }
}

/**
 * Also listen for storage events so changes in other tabs propagate
 * (e.g. if the user changes autonomy in one tab, other tabs update live).
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (ev) => {
    if (ev.key === STORAGE_KEY) {
      notifyListeners();
    }
  });
}
